import React, { useCallback, useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { Box, Skeleton } from "@mui/material"
import "@syncfusion/ej2/material.css"
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  Legend,
  Category,
  Tooltip,
  DataLabel,
  StepLineSeries,
} from "@syncfusion/ej2-react-charts"
import i18n from "i18n"

import OverviewCard, { OVERVIEW_CHART_CARD_MIN_HEIGHT_PX } from "./OverviewCard"
import { formatChartNumber } from "./utils/numberFormat"

/** Misma Y que el punto anterior → no es un hito de esa serie (solo comparte fecha del eje común). */
const getPreviousPoint = (point, series) => {
  const idx = point?.index
  if (idx == null || idx <= 0) return null
  return series?.points?.[idx - 1] ?? null
}

const getPointY = point => {
  const n = Number(point?.yValue ?? point?.y)
  return Number.isFinite(n) ? n : 0
}

const isFlatStepVsPrevious = (point, series) => {
  const prev = getPreviousPoint(point, series)
  if (!prev) return false
  const y = getPointY(point)
  const py = getPointY(prev)
  return Number.isFinite(y) && Number.isFinite(py) && Math.abs(y - py) < 0.005
}

const formatSignedDelta = delta => {
  if (!Number.isFinite(delta) || Math.abs(delta) < 0.005) {
    return formatChartNumber(0)
  }
  if (delta > 0) return `+${formatChartNumber(delta)}`
  return formatChartNumber(delta)
}

const LineChartCard = ({ title, seriesData, height, isLoading, currency }) => {
  const containerRef = useRef(null)
  const chartRef = useRef(null)

  const requestChartRefresh = useCallback(() => {
    chartRef.current.refresh()
  }, [])

  useEffect(() => {
    if (isLoading) return
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(requestChartRefresh)
    })
    ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [isLoading, requestChartRefresh, seriesData, height])

  const handleAxisLabelRender = args => {
    if (args?.axis?.orientation === "Vertical") {
      args.text = formatChartNumber(args.value, 0)
    }
  }

  const handleTooltipRender = args => {
    if (!args?.point) return
    const point = args.point
    const series = args.series
    const xLabel = point.x
    const y = getPointY(point)
    const total = `${formatChartNumber(y)} ${currency ?? ""}`.trim()
    const prev = getPreviousPoint(point, series)
    const deltaPart = prev
      ? `${formatSignedDelta(y - getPointY(prev))} ${currency ?? ""}`.trim()
      : "—"
    args.text = i18n.t("overview.chartTooltipEvolutionPoint", {
      date: xLabel,
      total,
      deltaPart,
    })
  }

  const handleTextRender = args => {
    if (isFlatStepVsPrevious(args?.point, args?.series)) {
      args.cancel = true
      return
    }
    if (args?.point) {
      args.text = `${formatChartNumber(args.point.y)} ${currency}`
    }
  }

  const handlePointRender = args => {
    if (isFlatStepVsPrevious(args?.point, args?.series)) {
      args.cancel = true
    }
  }

  const chartHeight = height || 300

  return (
    <OverviewCard
      title={title}
      loading={isLoading}
      sx={{ minHeight: OVERVIEW_CHART_CARD_MIN_HEIGHT_PX }}
    >
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: chartHeight,
          minWidth: 0,
        }}
      >
        {isLoading ? (
          <Skeleton variant="rectangular" width="100%" height={chartHeight} />
        ) : (
          <ChartComponent
            ref={chartRef}
            id="charts"
            width="100%"
            height={`${chartHeight}px`}
            enableResize={true}
            locale="es"
            useGroupingSeparator={true}
            chartArea={{ border: { width: 0 } }}
            primaryXAxis={{
              valueType: "Category",
              title: i18n.t("overview.chartAxisDateTitle"),
              labelIntersectAction: "Rotate45",
            }}
            primaryYAxis={{
              labelFormat: "n0",
            }}
            axisLabelRender={handleAxisLabelRender}
            tooltipRender={handleTooltipRender}
            textRender={handleTextRender}
            pointRender={handlePointRender}
            legendSettings={{ visible: true }}
            tooltip={{
              enable: true,
            }}
          >
            <Inject
              services={[StepLineSeries, Legend, Tooltip, DataLabel, Category]}
            />
            <SeriesCollectionDirective>
              {seriesData.map(serie => (
                <SeriesDirective
                  key={serie.name}
                  dataSource={serie.data}
                  xName="x"
                  yName="y"
                  fill={serie.color}
                  width={2}
                  name={serie.name}
                  type="StepLine"
                  marker={{ visible: true, shape: "Circle" }}
                  dataLabel={{
                    visible: true,
                    position: "Top",
                    font: { size: "12px" },
                  }}
                />
              ))}
            </SeriesCollectionDirective>
          </ChartComponent>
        )}
      </Box>
    </OverviewCard>
  )
}

LineChartCard.propTypes = {
  title: PropTypes.string.isRequired,
  xData: PropTypes.array,
  seriesData: PropTypes.array.isRequired,
  width: PropTypes.number,
  height: PropTypes.number,
  isLoading: PropTypes.bool,
  currency: PropTypes.string,
}

export default LineChartCard
